/**
 * The token drag's pure decisions (`projects/off-turn-planning.md` 2.3, 2.4, 3.2), pulled out of
 * `CombatStage` so the one rule that matters here unit-tests without Pixi: **a planning drag never
 * reaches a commit path**. A planning drag is a PLAY drag of a unit the viewer may control but may
 * not move right now (off-turn); it shows the ghost, the path dots and the forecast, and its release
 * dispatches nothing.
 */
import { isRefusedForecast, MOVE_FORECAST_COPY, moveForecastText, type MoveForecast, type MoveForecastEntry } from "@lancer-vtt/rules";

/** What kind of drag a press opens: `commit` (EDIT placement, or a PLAY move the room may accept), `planning` (show only), or `null` (no drag). */
export type DragKind = "commit" | "planning";

export function dragKindFor(args: {
  /** GM-inclusive control of the unit. */
  controls: boolean;
  /** EDIT mode's `place_token` authoring drag. */
  placing: boolean;
  /** The commit gate (`mayPlayMove` over `previewIfControlled`). */
  canPlayMove: boolean;
  /** `planningStateFor(...).planning`: the viewer may plan this unit's move but not commit it. */
  planning: boolean;
}): DragKind | null {
  const { controls, placing, canPlayMove, planning } = args;
  if (!controls) return null;
  if (placing || canPlayMove) return "commit";
  return planning ? "planning" : null;
}

/**
 * How a released drag resolves. `click`: the press never travelled (select only, nothing to tear
 * down). `planning`: clear the ghost, nothing else. `snap-back`: the drop resolved to the origin
 * cell. `commit`: dispatch the move. `planning` is decided before the cell compare, so no planning
 * drop can fall through to `commit`.
 */
export type DropOutcome = "click" | "planning" | "snap-back" | "commit";

export function dropOutcome(args: { kind: DragKind; moved: boolean; sameCell: boolean }): DropOutcome {
  if (!args.moved) return "click";
  if (args.kind === "planning") return "planning";
  return args.sameCell ? "snap-back" : "commit";
}

/** A planning drag must not stream its trail: other clients would read it as an impending move. */
export function streamsMovePreview(kind: DragKind): boolean {
  return kind === "commit";
}

/** The callout's heading on a planning drag. */
export function planningHeading(activeUnitName: string | null): string {
  return activeUnitName === null ? "Between turns — planning only" : `${activeUnitName}'s turn — planning only`;
}

export type ForecastRow = { key: string; kind: MoveForecastEntry["kind"] | "moves_left" | "boost_riders"; text: string };

/**
 * The callout's rows (`move-advisory` 2.1): the moves left after the drop first, then one row per
 * forecast entry in `MOVE_FORECAST_COPY`'s words, reactions flagged. Names only; no weapon or threat
 * numbers. A refused drop (a Boost it needs and cannot take) shows only its reason, with no moves-left line.
 *
 * **Only what this destination causes** (`decisions/0186` item 3). Every row comes from a forecast
 * entry, and `forecastMove` emits an entry only for a fact another destination might not share, so
 * nothing true of every move ever reaches the callout: not "moving closes the Protocol window", not
 * "moving closes Ordnance", not "moving costs movement". The moves-left line is a number that
 * differs per destination, not a reminder that moving costs movement. Do not add a row here that
 * does not come from an entry.
 */
export function forecastRows(forecast: MoveForecast | null): ForecastRow[] {
  if (forecast === null) return [];
  const rows: ForecastRow[] = isRefusedForecast(forecast) ? [] : [{ key: "moves-left", kind: "moves_left", text: MOVE_FORECAST_COPY.movesLeft(forecast.moveLeftAfter) }];
  for (const e of forecast.entries) rows.push({ key: rowKey(e), kind: e.kind, text: e.kind === "reaction" ? `⚠ ${moveForecastText(e)}` : moveForecastText(e) });
  return rows;
}

function rowKey(e: MoveForecastEntry): string {
  switch (e.kind) {
    case "boost":
    case "boost_refused":
      return e.kind;
    case "reaction":
      return `${e.consumerId}:${e.unitId}:${e.reactionName}`;
    case "reachable_by":
    case "trample":
      return e.kind;
    default:
      return `${e.kind}:${e.unitId}`;
  }
}

/**
 * The drag hint for a drop that folds a Boost with opt-in riders (`move-family-opt-in-surface`
 * Phase 1): informational only, since the pointer is held. The drop opens the Boost card that asks.
 */
export function boostRidersHint(riders: readonly { label: string }[]): string {
  return `BOOST · ${riders.map((r) => r.label.toUpperCase()).join(", ")} available`;
}

/**
 * What the callout shows for one drag cell, or `null` to hide it: a planning drag always shows
 * (heading + rows); a real drag whenever the drop is legal. `boostRiders` are the riders the drop's
 * folded Boost would ask about (`boostDropRiders`); they add the {@link boostRidersHint} row.
 */
export function calloutContent(args: {
  kind: DragKind;
  activeUnitName: string | null;
  forecast: MoveForecast | null;
  boostRiders?: readonly { label: string }[] | null;
}): { heading: string | null; rows: ForecastRow[] } | null {
  const rows = forecastRows(args.forecast);
  if (args.boostRiders && args.boostRiders.length > 0) rows.push({ key: "boost-riders", kind: "boost_riders", text: boostRidersHint(args.boostRiders) });
  if (args.kind === "planning") return { heading: planningHeading(args.activeUnitName), rows };
  return rows.length > 0 ? { heading: null, rows } : null;
}

/** A Trampler drag's drop is refused when its trail has no forecast (over the move left, even after a Boost) or a refused one. */
export function trailRefused(forecast: MoveForecast | null): boolean {
  return forecast === null || isRefusedForecast(forecast);
}

/** Who a Trampler drag's trail tramples, off its forecast's `trample` entry: the characters the drag outlines. */
export function trampledIds(forecast: MoveForecast | null): string[] {
  const entry = forecast?.entries.find((e) => e.kind === "trample");
  return entry?.kind === "trample" ? entry.unitIds : [];
}
